import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, KeyRound, CheckCircle2, ArrowLeft, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CpResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (pw !== confirm) { setError("Passwords don't match."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/client-portal/auth/reset-password`,
                       { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/client-portal/login"), 2500);
    } catch (err) {
      const msg = err?.response?.data?.detail
        || "Could not reset password. The link may have expired — please request a new one.";
      setError(msg);
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="bg-white border border-slate-200 rounded-sm p-10 max-w-md text-center" data-testid="cp-reset-success">
          <CheckCircle2 size={40} className="mx-auto text-emerald-500 mb-3" />
          <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: "Outfit" }}>
            Password updated
          </h2>
          <p className="text-sm text-slate-600">
            You can now sign in with your new password. Redirecting…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <div className="hidden md:flex md:w-1/2 bg-[#0a2540] text-white items-center justify-center p-12">
        <div className="max-w-md">
          <h1 className="text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: "Outfit" }}>
            Choose a new<br />password.
          </h1>
          <p className="text-sm text-slate-300 leading-relaxed">
            Use at least 8 characters. We recommend a mix of letters, numbers and symbols.
          </p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <Link to="/client-portal/login" className="text-xs text-slate-500 hover:text-[#0a2540] inline-flex items-center gap-1 mb-6">
            <ArrowLeft size={11} /> Back to sign in
          </Link>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <h2 className="text-xs tracking-[0.18em] uppercase text-slate-500 font-semibold">Reset password</h2>
              <h1 className="text-2xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>
                Set your new password
              </h1>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cp-rp-pw">New password</Label>
              <Input
                id="cp-rp-pw" type="password" required minLength={8}
                value={pw} onChange={(e) => setPw(e.target.value)}
                placeholder="At least 8 characters"
                autoComplete="new-password"
                data-testid="cp-reset-new-password"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cp-rp-confirm">Confirm password</Label>
              <Input
                id="cp-rp-confirm" type="password" required minLength={8}
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                placeholder="Repeat the new password"
                autoComplete="new-password"
                data-testid="cp-reset-confirm-password"
              />
            </div>
            {error && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2 flex items-start gap-2" data-testid="cp-reset-error">
                <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <Button
              type="submit"
              disabled={submitting || !pw || !confirm}
              className="w-full bg-[#0a2540] hover:bg-[#0a2540]/90"
              data-testid="cp-reset-submit"
            >
              {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : <KeyRound size={16} className="mr-2" />}
              Update password
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
